import { z } from "zod";

import { COUNTRY_CODES } from "../constants/countries.js";
import { LICENCE_STATUSES } from "../constants/licence-status.js";

/**
 * A licence's client category key (#564): a row of `client_categories` (#604).
 * The list lives in the database, so the schema only checks the shape; whether
 * the key exists is the use-case's question (400 when it does not).
 */
export const ClientCategoryValueSchema = z.string().min(1);

/** One entry of the client category list (#604): the key a licence stores, and its wording. */
export const ClientCategorySchema = z.object({
    value: z.string(),
    label: z.string(),
});
export type ClientCategory = z.infer<typeof ClientCategorySchema>;

/** The longest label an Administrator may give a new category (#604). */
export const CLIENT_CATEGORY_LABEL_MAX_LENGTH = 60;

/**
 * `POST /licences/client-categories` (#604). No `max` here on purpose: a label too
 * long is answered by the use-case with its own message, like an empty one.
 */
export const CreateClientCategoryRequestSchema = z.object({
    label: z.string(),
});
export type CreateClientCategoryRequest = z.infer<typeof CreateClientCategoryRequestSchema>;

export const ClientCategoryListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(ClientCategorySchema),
});
export type ClientCategoryListResponse = z.infer<typeof ClientCategoryListResponseSchema>;

export const ClientCategoryDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ClientCategorySchema,
});
export type ClientCategoryDetailResponse = z.infer<typeof ClientCategoryDetailResponseSchema>;

/** An ISO 3166-1 alpha-2 country code: one of `COUNTRY_CODES` (#564). */
export const CountryCodeSchema = z.enum(COUNTRY_CODES);

/** Whether a licence may be used: one of `LICENCE_STATUSES` (#582). */
export const LicenceStatusSchema = z.enum(LICENCE_STATUSES);

/** A calendar day, `YYYY-MM-DD` (#750). */
export const DateOnlySchema = z.iso.date();

/**
 * Profiles Invyo delivered to a licence over one contract period (#750): the
 * distinct profiles that first reached it in an Invyo list with the `sent`
 * status between `period_start` and `period_end` (both included, `YYYY-MM-DD`),
 * against the yearly target that period had (null when none was set).
 */
export const ProfilesDeliveredSchema = z.object({
    period_index: z.number().int(),
    period_start: DateOnlySchema,
    period_end: DateOnlySchema,
    delivered: z.number().int(),
    target: z.number().int().nullable(),
});
export type ProfilesDelivered = z.infer<typeof ProfilesDeliveredSchema>;

export const LicenceResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    /** Licence ownership was removed (#647): always null, kept in the contract for API clients. */
    owner_id: z.uuid().nullable(),
    opening_date: z.string(),
    status: LicenceStatusSchema,
    user_count: z.number().int(),
    /**
     * How many of `user_count` actually use Invyo (#557): client accounts only
     * (no Invyo staff), `subscription_status = active` and not archived.
     *
     * The row carries it so the "Licences without users" figure of the KPI band
     * and the rows that figure filters to are counted by the SAME rule: two
     * queries counting the same thing differently is what PI-105 was.
     */
    active_user_count: z.number().int(),
    list_count: z.number().int(),
    /**
     * The most recent `sent_at` across the licence's lists (#560), or null when
     * none has a recorded send date.
     */
    last_list_sent_at: z.string().nullable(),
    /**
     * When an Invyo admin last did business on the licence (#559): renamed it,
     * created or duplicated a list for it, launched an enrichment on it, added a
     * user to it. Null when nothing was recorded.
     */
    last_admin_activity_at: z.string().nullable(),
    /**
     * True for licences that existed before activity tracking shipped (#559): a
     * null `last_admin_activity_at` there means "not recorded", not "never used".
     */
    created_before_activity_tracking: z.boolean(),
    /**
     * The most recent `last_login_date` among the licence's client users; admins
     * and archived accounts excluded (#559). Null when none has signed in.
     */
    last_client_login_at: z.string().nullable(),
    /** The kind of client, as its key (#564). Null on licences created before the field existed. */
    client_category: ClientCategoryValueSchema.nullable(),
    /**
     * That category's wording (#604), read from `client_categories` with the row,
     * so a screen shows it without loading the list. Null exactly when
     * `client_category` is.
     */
    client_category_label: z.string().nullable(),
    /** Where the client is based, as an ISO alpha-2 code (#564). Null on older licences. */
    based_in: CountryCodeSchema.nullable(),
    /**
     * The contract (#750), Invyo's only: `GET /licences/current` leaves these
     * three out for a client user. The start date is a calendar day
     * (`YYYY-MM-DD`), null until an Administrator enters it, like the target.
     */
    contract_start_date: DateOnlySchema.nullable().optional(),
    annual_profiles_target: z.number().int().nullable().optional(),
    /** The running contract period's figures; null without a start date, or before it. */
    profiles_delivered: ProfilesDeliveredSchema.nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
});
export type LicenceResponse = z.infer<typeof LicenceResponseSchema>;

/** One of a licence's main contacts (#564): a user of that licence. */
export const PrimaryContactSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
});
export type PrimaryContact = z.infer<typeof PrimaryContactSchema>;

/**
 * The Invyo Customer Success Manager who follows a licence (#583): an admin user,
 * named like a main contact. Whoever is on record is returned as is: someone
 * archived since they were picked stays the licence's
 * CSM until an Administrator picks someone else.
 *
 * `email` is the Invyo team's only (#695): `GET /licences/current` leaves it out
 * for a client user, who reaches their CSM through the contact form instead.
 */
export const LicenceCsmSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string().optional(),
    /** Their profile photo (#579), shown on the Onboarding tab's "Invyo sales" card. */
    avatar_url: z.string().nullable(),
});
export type LicenceCsm = z.infer<typeof LicenceCsmSchema>;

/**
 * A single licence as the one-licence endpoints return it (read, create, edit):
 * the row plus its main contacts and its CSM. The index keeps `LicenceResponse`:
 * a list of every licence has no use for every licence's contacts.
 */
export const LicenceDetailSchema = LicenceResponseSchema.extend({
    primary_contacts: z.array(PrimaryContactSchema),
    /** Null until an Administrator picks one (#583). */
    csm: LicenceCsmSchema.nullable(),
});
export type LicenceDetail = z.infer<typeof LicenceDetailSchema>;

/**
 * `PATCH /licences/:id`. Two callers, two shapes, one schema:
 *  - an Administrator edits the whole identity: `name`, `client_category` and
 *    `based_in`, all three required;
 *  - an owner renames their own licence: `name` alone (the `/licence` page).
 * The identity fields are optional HERE because which shape is allowed depends on
 * who is asking, which only the use-case knows: `decideLicenceEdit` refuses an
 * owner who sends them (403) and an Administrator who leaves one out (400). Each
 * is still validated whenever it is sent: the category against the list in
 * the database, by the use-case (#604).
 *
 * `csm_user_id` (#583) is the Administrator's too, and required of them since
 * #695 (every licence names a CSM, whom the client's contact form writes to):
 * an Administrator's edit without it is refused (400). It must be an eligible
 * CSM unless they are the one already on record (400).
 */
export const UpdateLicenceRequestSchema = z.object({
    name: z.string().min(1),
    client_category: ClientCategoryValueSchema.optional(),
    based_in: CountryCodeSchema.optional(),
    csm_user_id: z.uuid().optional(),
    /**
     * The contract (#750), an Administrator's fields like the identity, but
     * optional for them: absent leaves the value, `null` clears it.
     */
    contract_start_date: DateOnlySchema.nullable().optional(),
    annual_profiles_target: z.number().int().min(1).nullable().optional(),
});
export type UpdateLicenceRequest = z.infer<typeof UpdateLicenceRequestSchema>;

export const CreateLicenceRequestSchema = z.object({
    name: z.string().min(1),
    client_category: ClientCategoryValueSchema,
    based_in: CountryCodeSchema,
    /** Required since #695: an eligible CSM (#583). */
    csm_user_id: z.uuid(),
});
export type CreateLicenceRequest = z.infer<typeof CreateLicenceRequestSchema>;

/**
 * `PUT /admin/licences/:id/primary-contacts`: the complete set of main contacts,
 * replacing the current one. No `max` here on purpose: more than
 * `MAX_PRIMARY_CONTACTS` is a business rule, answered with its own message by the
 * use-case rather than a generic schema error.
 */
export const SetPrimaryContactsRequestSchema = z.object({
    user_ids: z.array(z.uuid()),
});
export type SetPrimaryContactsRequest = z.infer<typeof SetPrimaryContactsRequestSchema>;

/**
 * `PATCH /admin/licences/:id/status`: deactivate or reactivate a licence (#582).
 *
 * The target state is sent, not a toggle: replaying the same call twice leaves the
 * licence where it already is instead of flipping it back.
 */
export const SetLicenceStatusRequestSchema = z.object({
    status: LicenceStatusSchema,
});
export type SetLicenceStatusRequest = z.infer<typeof SetLicenceStatusRequestSchema>;

export const PrimaryContactsResponseSchema = z.object({
    primary_contacts: z.array(PrimaryContactSchema),
});
export type PrimaryContactsResponse = z.infer<typeof PrimaryContactsResponseSchema>;

export const PrimaryContactsDetailResponseSchema = z.object({
    success: z.boolean(),
    data: PrimaryContactsResponseSchema,
});
export type PrimaryContactsDetailResponse = z.infer<typeof PrimaryContactsDetailResponseSchema>;

export const LicenceListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(LicenceResponseSchema),
});
export type LicenceListResponse = z.infer<typeof LicenceListResponseSchema>;

export const LicenceDetailResponseSchema = z.object({
    success: z.boolean(),
    data: LicenceDetailSchema,
});
export type LicenceDetailResponse = z.infer<typeof LicenceDetailResponseSchema>;

/**
 * `PATCH /admin/licences/:id/status`: the licence as it now stands, plus how many
 * sessions the deactivation actually cut (#582).
 *
 * `revoked_sessions` lets the interface report what happened instead of promising
 * it: it is 0 on a reactivation, and 0 on a deactivation of a licence whose users
 * had no live session.
 */
export const SetLicenceStatusResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        licence: LicenceDetailSchema,
        revoked_sessions: z.number().int(),
    }),
});
export type SetLicenceStatusResponse = z.infer<typeof SetLicenceStatusResponseSchema>;

/** The longest internal notes a licence may carry (#605): room for years of exchanges, not a document store. */
export const LICENCE_NOTES_MAX_LENGTH = 20_000;

/** Who last saved a licence's internal notes (#605). */
export const LicenceNotesAuthorSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
});
export type LicenceNotesAuthor = z.infer<typeof LicenceNotesAuthorSchema>;

/**
 * `GET` / `PUT /admin/licences/:id/notes`: the Invyo team's internal notes on a
 * licence (#605): one plain-text block, line breaks kept. Admin-only, and on
 * purpose NOT a field of `LicenceDetailSchema`, which `GET /licences/current`
 * also sends to the client.
 */
export const LicenceNotesSchema = z.object({
    /** Null until an admin writes some, and again once they are cleared. */
    internal_notes: z.string().nullable(),
    /** When they were last saved, a clearing included; null if never. */
    updated_at: z.string().nullable(),
    /** Who last saved them; null if never, or once that user is deleted. */
    updated_by: LicenceNotesAuthorSchema.nullable(),
});
export type LicenceNotes = z.infer<typeof LicenceNotesSchema>;

/** `PUT /admin/licences/:id/notes`: the whole block, replacing it. Blank clears it. */
export const UpdateLicenceNotesRequestSchema = z.object({
    internal_notes: z.string().max(LICENCE_NOTES_MAX_LENGTH),
});
export type UpdateLicenceNotesRequest = z.infer<typeof UpdateLicenceNotesRequestSchema>;

export const LicenceNotesDetailResponseSchema = z.object({
    success: z.boolean(),
    data: LicenceNotesSchema,
});
export type LicenceNotesDetailResponse = z.infer<typeof LicenceNotesDetailResponseSchema>;

// Org-wide aggregate figures for the admin KPI band. A separate contract from
// the row list because the figures do not move between pages: they count every
// licence, not the ones currently loaded.
export const LicenceStatsResponseSchema = z.object({
    /** Every licence, whatever its status. Kept for callers that predate #557. */
    total: z.number().int(),
    /** Every user row, Invyo staff and archived accounts included. Kept for the same reason. */
    total_users: z.number().int(),
    /** Every list, whatever its licence. Kept for the same reason. */
    total_lists: z.number().int(),
    /** Licences opened this month, whatever their status. Kept for the same reason. */
    new_this_month: z.number().int(),

    // ─── What the band shows (#557) ────────────────────────────────────────
    // All four count the CLIENT side of an ACTIVE licence, and nothing else:
    // `licence.status = 'active'`, and for people `is_admin = false`,
    // `subscription_status = 'active'`, `archived_at IS NULL`. One definition of
    // "the client", the same one "Last client login" already uses (#559): the
    // band would otherwise report Invyo's own staff as client usage.

    /** Licences whose status is `active`. */
    active_licences: z.number().int(),
    /** Client users who can actually sign in, across active licences. */
    active_users: z.number().int(),
    /** Lists belonging to an active licence. */
    active_lists: z.number().int(),
    /**
     * Active licences with no active client user: the clients who never
     * onboarded. Clicking the figure filters the table to exactly these rows.
     */
    licences_without_users: z.number().int(),

    // ─── The captions under the figures (#558) ─────────────────────────────
    // "Created or activated this month", never a month-over-month delta: nothing
    // dates a deactivation or the arrival of a licence's first user, so a true
    // "vs last month" on the first and fourth figures would need a monthly
    // snapshot. Decided with the PO on #558.

    /** Active licences opened since the 1st of the current month (UTC). */
    new_licences_this_month: z.number().int(),
    /** Active client users whose subscription started this month (UTC). */
    new_users_this_month: z.number().int(),
    /** Lists of active licences created this month (UTC). */
    new_lists_this_month: z.number().int(),
});
export type LicenceStatsResponse = z.infer<typeof LicenceStatsResponseSchema>;

export const LicenceStatsDetailResponseSchema = z.object({
    success: z.boolean(),
    data: LicenceStatsResponseSchema,
});
export type LicenceStatsDetailResponse = z.infer<typeof LicenceStatsDetailResponseSchema>;

// Name-availability probe for the add-licence form. The comparison is
// case-insensitive on the trimmed name; a blank name answers `false` rather
// than erroring, so the caller never has to special-case empty input.
export const LicenceExistsResponseSchema = z.object({
    exists: z.boolean(),
});
export type LicenceExistsResponse = z.infer<typeof LicenceExistsResponseSchema>;

export const LicenceExistsDetailResponseSchema = z.object({
    success: z.boolean(),
    data: LicenceExistsResponseSchema,
});
export type LicenceExistsDetailResponse = z.infer<typeof LicenceExistsDetailResponseSchema>;

/** The longest message a client may send their CSM (#695). */
export const CSM_MESSAGE_MAX_LENGTH = 5_000;

/**
 * `POST /licences/current/csm-message` (#695): the client's message to the
 * Customer Success Manager of their licence, sent by email on their behalf.
 */
export const CsmMessageRequestSchema = z.object({
    message: z.string().min(1).max(CSM_MESSAGE_MAX_LENGTH),
});
export type CsmMessageRequest = z.infer<typeof CsmMessageRequestSchema>;

/**
 * `GET /admin/licences/:id/contract-history` (#750): every contract period up to
 * the running one, newest first, with what was delivered and the target it had;
 * and the journal of the contract fields, newest first.
 */
export const ContractChangeSchema = z.object({
    field: z.enum(["contract_start_date", "annual_profiles_target"]),
    previous_value: z.string().nullable(),
    new_value: z.string().nullable(),
    changed_at: z.string(),
    /** Who made it; null once that user is deleted. */
    changed_by: LicenceNotesAuthorSchema.nullable(),
});
export type ContractChange = z.infer<typeof ContractChangeSchema>;

export const ContractHistorySchema = z.object({
    periods: z.array(ProfilesDeliveredSchema),
    changes: z.array(ContractChangeSchema),
});
export type ContractHistory = z.infer<typeof ContractHistorySchema>;

export const ContractHistoryDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ContractHistorySchema,
});
